import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { CONTACT_INFO } from '../data/mockData';
import PushNotificationManager from '../components/PushNotificationManager';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit form');
      }

      toast.success(data.message || 'Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to send message. Please try calling us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-[#111216] pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get In <span className="text-[#B8860B]">Touch</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Have questions about our services? Want to discuss your vehicle's specific needs? 
            We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Contact Cards */}
            <div className="space-y-6">
              {/* Phone */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#B8860B]/30 transition-all">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Call Us</h3>
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="text-gray-400 hover:text-[#B8860B] transition-colors"
                >
                  {CONTACT_INFO.phone}
                </a>
              </div>

              {/* Email */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#B8860B]/30 transition-all">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Email Us</h3>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-gray-400 hover:text-[#B8860B] transition-colors break-words"
                >
                  {CONTACT_INFO.email}
                </a>
              </div>

              {/* Location */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#B8860B]/30 transition-all">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Location</h3>
                <p className="text-gray-400">{CONTACT_INFO.address}</p>
                <p className="text-gray-500 text-sm mt-2">{CONTACT_INFO.serviceRadius}</p>
              </div>

              {/* Hours */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#B8860B]/30 transition-all">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Hours</h3>
                <div className="text-gray-400 space-y-1 text-sm">
                  <p><span className="text-white">Mon-Fri:</span> {CONTACT_INFO.hours.weekday}</p>
                  <p><span className="text-white">Sat-Sun:</span> {CONTACT_INFO.hours.weekend}</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h2 className="text-3xl font-bold text-white mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-white">Subject</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="What's this about?"
                        className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us how we can help..."
                      rows={6}
                      className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-[#B8860B] to-[#C8960B] hover:from-[#C8960B] hover:to-[#B8860B] text-[#111216] font-bold text-lg py-6"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-b from-[#111216] to-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-[#B8860B]/10 to-[#C8960B]/10 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-16 h-16 text-[#B8860B] mx-auto mb-4" />
                  <p className="text-white font-semibold text-lg mb-2">{CONTACT_INFO.address}</p>
                  <p className="text-gray-400 text-sm">{CONTACT_INFO.serviceRadius}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Service Areas Section */}
      <section className="py-20 bg-gradient-to-b from-[#111216] to-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Areas We <span className="text-[#B8860B]">Serve</span>
              </h2>
              <p className="text-gray-400 text-lg">
                Premium mobile detailing services across Los Angeles and Ventura County
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {CONTACT_INFO.serviceAreas.map((area) => (
                  <div
                    key={area}
                    className="text-center p-4 bg-white/5 rounded-xl hover:bg-[#B8860B]/10 hover:border-[#B8860B]/30 border border-transparent transition-all"
                  >
                    <p className="text-white font-semibold">{area}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 text-center">
                <p className="text-gray-400 mb-4">
                  <span className="text-[#B8860B] font-semibold">Mobile Service Available</span> within 25-mile radius
                </p>
                <p className="text-gray-500 text-sm">
                  Don't see your area? Call us at <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-[#B8860B] hover:underline">{CONTACT_INFO.phone}</a> to check availability
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Push Notifications Section */}
      <section className="py-20 bg-[#1A1A1F]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Never Miss an <span className="text-[#B8860B]">Appointment</span>
              </h2>
              <p className="text-gray-400 text-lg">
                Get timely reminders and exclusive offers delivered straight to your device
              </p>
            </div>

            <div className="flex justify-center">
              <PushNotificationManager userId="guest" />
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1F] to-[#111216]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Follow Our <span className="text-[#B8860B]">Work</span>
            </h2>
            <p className="text-gray-400 text-lg mb-12">
              See our latest projects and transformations on social media
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a
                href={CONTACT_INFO.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 transition-all group"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-[#B8860B]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Instagram</h3>
                <p className="text-gray-400 text-sm">@thedetailingdisciples</p>
              </a>

              <a
                href={CONTACT_INFO.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 transition-all group"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-[#B8860B]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Facebook</h3>
                <p className="text-gray-400 text-sm">Detailing Disciples</p>
              </a>

              <a
                href={CONTACT_INFO.socialMedia.yelp}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 transition-all group"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-[#B8860B]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21.111 18.226c-.141.969-2.119 3.483-3.029 3.847-.311.124-.611.094-.85-.09-.154-.12-.314-.365-2.447-3.827l-.633-1.032c-.244-.4-.199-.857.104-1.229a.665.665 0 0 1 .341-.22l5.004-.711c.844-.119 1.665.197 1.931.844.103.251.128.565-.421 2.418zm-9.611 1.214c-1.065-.047-3.827-.591-4.531-1.213a.841.841 0 0 1-.268-.854c.063-.19.234-.438 2.847-4.091l.771-.997c.31-.405.797-.54 1.249-.382.147.051.277.137.382.25l3.423 3.847c.597.671.663 1.567.159 2.218-.196.254-.534.545-1.829.784-1.26.232-1.834.459-2.203.438zm-2.713-7.483L6.658 3.813c-.152-.848.326-1.83 1.164-2.287.323-.176.741-.272 1.396-.229 1.027.071 3.829 1.239 4.453 2.047.212.277.234.581.113.851-.078.175-.263.389-3.432 3.107l-.839.765c-.368.335-.898.375-1.329.086a.667.667 0 0 1-.266-.314zm7.569-1.899c.589-.509 1.562-.594 2.24-.185.263.159.532.475 1.619 2.257.527.862.789 1.299.858 1.574.124.491.028.859-.288 1.096-.206.154-.479.27-3.949 1.481l-1.162.405c-.455.159-.973.038-1.319-.306a.664.664 0 0 1-.177-.36l-1.011-5.031c-.143-.714.07-1.463.546-1.856.184-.153.425-.288.934-.446 1.247-.387 1.455-.531 1.709-.629z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Yelp</h3>
                <p className="text-gray-400 text-sm">5.0 ⭐ Reviews</p>
              </a>

              <a
                href={CONTACT_INFO.socialMedia.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#B8860B]/50 transition-all group"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8860B]/20 to-[#C8960B]/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-[#B8860B]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">TikTok</h3>
                <p className="text-gray-400 text-sm">@detailing.discipl</p>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
